/**
 * Analytics Endpoints
 */
import { getApiClient } from "../client";
import { WARNING_POPULATE } from "../populate";
import { AdminReferrerStatsSchema, AppConfigSchema, AuthLogSchema, AuthLogStatsSchema, BotsPerCountrySchema, DailySignupsDataSchema, EmailLogSchema, EmailStatsSchema, EmailSystemStatusSchema, EmailTemplatePreviewSchema, EmailTemplateSchema, EmailTemplateVariablesSchema, EmailTestPreviewSchema, EmailTestSearchUserSchema, EmailTriggerMetadataSchema, GroupedEmailTemplatesSchema, MonthlyChargesDataSchema, ReferrerHierarchyStatsSchema, StatsPerReferrerSchema, UsersPerCountrySchema, validateResponse, WarningSchema, YearlyMonthlyPayoutsSchema, YearlySignupsDataSchema, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse } from "../utils/parseStrapiResponse";
/**
 * Admin statistics endpoints
 */
export const adminStatsEndpoints = {
    /**
     * Get yearly monthly charges.
     *
     * @param throughDay When given, every month also reports `chargedThroughDay`:
     *   completed charges from day 1 to this day-of-month. Pass the caller's own
     *   local day so a partial month can be compared against the same span of an
     *   earlier month.
     */
    async getYearMonthlyCharges(year, throughDay) {
        const response = await getApiClient().get(`/analytics/admin/charges/monthly/year/${year}`, {
            params: throughDay === undefined ? undefined : { throughDay },
        });
        return parseStrapiItemResponse(response.data, {
            schema: MonthlyChargesDataSchema,
            context: "adminStatsEndpoints.getYearMonthlyCharges",
        });
    },
    /**
     * Get monthly daily signups
     */
    async getMonthDailySignUps(month, params) {
        const response = await getApiClient().get(`/analytics/admin/signups/daily/month/${month}`, {
            params,
        });
        return parseStrapiItemResponse(response.data, {
            schema: DailySignupsDataSchema,
            context: "adminStatsEndpoints.getMonthDailySignUps",
        });
    },
    /**
     * Get yearly monthly signups - per month breakdown for a given year
     */
    async getYearMonthlySignUps(year) {
        const response = await getApiClient().get(`/analytics/admin/signups/monthly/year/${year}`);
        return parseStrapiItemResponse(response.data, {
            schema: YearlySignupsDataSchema,
            context: "adminStatsEndpoints.getYearMonthlySignUps",
        });
    },
    /**
     * Get users per country
     */
    async getUsersPerCountry(params) {
        const response = await getApiClient().get("/analytics/admin/users-per-country", { params });
        return parseStrapiItemResponse(response.data, {
            schema: UsersPerCountrySchema,
            context: "adminStatsEndpoints.getUsersPerCountry",
        });
    },
    /**
     * Get bots per country
     */
    async getBotsPerCountry(params) {
        const response = await getApiClient().get("/analytics/admin/bots-per-country", { params });
        return parseStrapiItemResponse(response.data, {
            schema: BotsPerCountrySchema,
            context: "adminStatsEndpoints.getBotsPerCountry",
        });
    },
    /**
     * Get referrer stats
     */
    async getReferrerStats(userId, params) {
        const response = await getApiClient().get(`/analytics/admin/referrer-stats/${userId}`, {
            params,
        });
        return parseStrapiItemResponse(response.data, {
            schema: AdminReferrerStatsSchema,
            context: "adminStatsEndpoints.getReferrerStats",
        });
    },
    /**
     * Get stats per referrer
     */
    async getStatsPerReferrer(params) {
        const response = await getApiClient().get("/analytics/admin/referrer-stats", { params });
        return parseStrapiItemResponse(response.data, {
            schema: StatsPerReferrerSchema,
            context: "adminStatsEndpoints.getStatsPerReferrer",
        });
    },
    /**
     * Get monthly full stats per bot
     */
    async getMonthlyFullStatsPerBot(params) {
        const response = await getApiClient().get("/analytics/admin/monthly-full-stats-per-bot", {
            params,
            responseType: "text",
        });
        if (typeof response.data !== "string" || !response.data.trim()) {
            throw new Error("Monthly stats export returned no CSV data");
        }
        return { data: response.data, meta: {} };
    },
    /**
     * Get referrers hierarchy stats
     */
    async getReferrersHierarchyStats(params) {
        const response = await getApiClient().get("/analytics/admin/referrer-stats/hierarchy", {
            params,
        });
        return parseStrapiListResponse(response.data, {
            schema: ReferrerHierarchyStatsSchema,
            context: "adminStatsEndpoints.getReferrersHierarchyStats",
        });
    },
    /**
     * Get yearly monthly payouts
     */
    async getYearlyMonthlyPayouts() {
        const response = await getApiClient().get("/analytics/admin/payouts/monthly");
        return parseStrapiItemResponse(response.data, {
            schema: YearlyMonthlyPayoutsSchema,
            context: "adminStatsEndpoints.getYearlyMonthlyPayouts",
        });
    },
};
/**
 * Email template endpoints
 */
export const emailTemplatesEndpoints = {
    /**
     * Get list of email templates grouped by trigger
     * Returns { data: { trigger1: [...], trigger2: [...] } }
     */
    async getList(params) {
        const response = await getApiClient().get("/email-templates", { params });
        return validateResponse(GroupedEmailTemplatesSchema, response.data, {
            context: "emailTemplatesEndpoints.getList",
        });
    },
    /**
     * Get email template by trigger
     */
    async getByTrigger(trigger) {
        const response = await getApiClient().get(`/email-templates/trigger/${trigger}`);
        return parseStrapiItemResponse(response.data, {
            schema: EmailTemplateSchema,
            context: "emailTemplatesEndpoints.getByTrigger",
        });
    },
    /**
     * Get template variables for a trigger
     */
    async getVariables(trigger) {
        const response = await getApiClient().get(`/email-templates/variables/${trigger}`);
        return parseStrapiItemResponse(response.data, {
            schema: EmailTemplateVariablesSchema,
            context: "emailTemplatesEndpoints.getVariables",
        });
    },
    /**
     * Update an email template
     */
    async update(id, input) {
        const response = await getApiClient().put(`/email-templates/${id}`, { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: EmailTemplateSchema,
            context: "emailTemplatesEndpoints.update",
        });
    },
    /**
     * Preview an email template
     */
    async preview(id, language) {
        const response = await getApiClient().post(`/email-templates/${id}/preview`, { language });
        return parseStrapiItemResponse(response.data, {
            schema: EmailTemplatePreviewSchema,
            context: "emailTemplatesEndpoints.preview",
        });
    },
    /**
     * Send a test email
     */
    async sendTest(id, recipientEmail) {
        const response = await getApiClient().post(`/email-templates/${id}/send-test`, {
            recipientEmail,
        });
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Toggle template active status
     */
    async toggleActive(id, isActive) {
        const response = await getApiClient().put(`/email-templates/${id}`, { data: { isActive } });
        return parseStrapiItemResponse(response.data, {
            schema: EmailTemplateSchema,
            context: "emailTemplatesEndpoints.toggleActive",
        });
    },
    /**
     * Get system status
     */
    async getSystemStatus() {
        const response = await getApiClient().get("/email-templates/testing/status");
        return parseStrapiItemResponse(response.data, {
            schema: EmailSystemStatusSchema,
            context: "emailTemplatesEndpoints.getSystemStatus",
        });
    },
    /**
     * Get all triggers with metadata (label, description, variables)
     */
    async getAllTriggers() {
        const response = await getApiClient().get("/email-templates/testing/triggers");
        return parseStrapiListResponse(response.data, {
            schema: EmailTriggerMetadataSchema,
            context: "emailTemplatesEndpoints.getAllTriggers",
        });
    },
    /**
     * Search users for testing
     */
    async searchUsers(query) {
        const response = await getApiClient().get("/email-templates/testing/search-users", { params: { query } });
        return parseStrapiListResponse(response.data, {
            schema: EmailTestSearchUserSchema,
            context: "emailTemplatesEndpoints.searchUsers",
        });
    },
    /**
     * Preview test email for a user
     */
    async previewTestEmail(trigger, userId) {
        const response = await getApiClient().get(`/email-templates/testing/preview/${trigger}`, {
            params: { userId },
        });
        return parseStrapiItemResponse(response.data, {
            schema: EmailTestPreviewSchema,
            context: "emailTemplatesEndpoints.previewTestEmail",
        });
    },
    /**
     * Send test email to a user
     */
    async sendTestToUser(input) {
        const response = await getApiClient().post(`/email-templates/testing/send/${input.trigger}`, {
            userId: input.userId,
        });
        return parseStrapiItemResponse(response.data);
    },
    /**
     * Send all test emails to a user
     */
    async sendAllTestsToUser(input) {
        const response = await getApiClient().post("/email-templates/testing/send-all", input);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Email log endpoints
 */
export const emailLogsEndpoints = {
    /**
     * Get list of email logs
     */
    async getList(params) {
        const response = await getApiClient().get("/email-logs", { params });
        return parseStrapiListResponse(response.data, {
            schema: EmailLogSchema,
            context: "emailLogsEndpoints.getList",
        });
    },
    /**
     * Get email log by ID
     */
    async getOne(id) {
        const response = await getApiClient().get(`/email-logs/${id}`);
        return parseStrapiItemResponse(response.data, {
            schema: EmailLogSchema,
            context: "emailLogsEndpoints.getOne",
        });
    },
    /**
     * Get email stats
     */
    async getStats(params) {
        const response = await getApiClient().get("/email-logs/stats", { params });
        return parseStrapiItemResponse(response.data, {
            schema: EmailStatsSchema,
            context: "emailLogsEndpoints.getStats",
        });
    },
    /**
     * Resend an email
     */
    async resend(id) {
        const response = await getApiClient().post(`/email-logs/${id}/resend`);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * Auth log endpoints
 */
export const authLogsEndpoints = {
    /**
     * Get list of auth logs
     */
    async getList(params) {
        const response = await getApiClient().get("/auth-logs", { params });
        return parseStrapiListResponse(response.data, {
            schema: AuthLogSchema,
            context: "authLogsEndpoints.getList",
        });
    },
    /**
     * Get auth log by ID
     */
    async getOne(id) {
        const response = await getApiClient().get(`/auth-logs/${id}`);
        return parseStrapiItemResponse(response.data, {
            schema: AuthLogSchema,
            context: "authLogsEndpoints.getOne",
        });
    },
    /**
     * Get auth stats
     */
    async getStats(params) {
        const response = await getApiClient().get("/auth-logs/stats", { params });
        return parseStrapiItemResponse(response.data, {
            schema: AuthLogStatsSchema,
            context: "authLogsEndpoints.getStats",
        });
    },
};
/**
 * Warning endpoints (admin)
 * Note: Dashboard uses /dashboard-warnings paths
 */
export const warningsEndpoints = {
    /**
     * Get warning by ID
     */
    async getWarning(id, params) {
        const populate = params?.populate ?? WARNING_POPULATE.DEFAULT;
        const response = await getApiClient().get(`/dashboard-warnings/${id}`, {
            params: { populate },
        });
        return parseStrapiItemResponse(response.data, {
            schema: WarningSchema,
            populate,
            context: "warningsEndpoints.getWarning",
        });
    },
    /**
     * Get all warnings
     */
    async getWarnings(params) {
        const populate = WARNING_POPULATE.DEFAULT;
        const response = await getApiClient().get("/dashboard-warnings", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: WarningSchema,
            populate,
            context: "warningsEndpoints.getWarnings",
        });
    },
    /**
     * Create a warning
     */
    async createWarning(input) {
        const response = await getApiClient().post("/dashboard-warnings", {
            data: input,
        });
        return parseStrapiItemResponse(response.data, {
            schema: WarningSchema,
            context: "warningsEndpoints.createWarning",
        });
    },
    /**
     * Update a warning
     */
    async updateWarning(id, input) {
        const response = await getApiClient().put(`/dashboard-warnings/${id}`, {
            data: input,
        });
        return parseStrapiItemResponse(response.data, {
            schema: WarningSchema,
            context: "warningsEndpoints.updateWarning",
        });
    },
    /**
     * Delete a warning
     */
    async deleteWarning(id) {
        const response = await getApiClient().delete(`/dashboard-warnings/${id}`);
        return parseStrapiItemResponse(response.data);
    },
};
/**
 * App configuration endpoints (admin)
 */
export const configEndpoints = {
    /**
     * Get app configuration
     */
    async get() {
        const response = await getApiClient().get("/config");
        return parseStrapiItemResponse(response.data, {
            schema: AppConfigSchema,
            context: "configEndpoints.get",
        });
    },
    /**
     * Update app configuration
     */
    async update(input) {
        const response = await getApiClient().put("/config", { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: AppConfigSchema,
            context: "configEndpoints.update",
        });
    },
};
